import { LocateFixed } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "src/components/ui/button";
import { DialogContent, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import { Input } from "src/components/ui/input";
import { ToastAlert } from "src/components/ui/toaster";
import { set_expertise_data } from "src/config/redux/slicer/expertise/expertiseSlicer";
import { getAddressFromCoords, getAddressLabel, DEFAULT_COORDS } from "src/config/services/fellas/google.api";
import { updateHealthCare } from "src/config/services/fellas/health-care.api";
import { getDragLocation } from "src/lib/utils";
import LeafletMapView from "src/components/map/LeafletMapView";
import LocationSearchInput, { PlaceSearchResult } from "src/components/map/LocationSearchInput";

function DialogLocationOffline() {
  const dispatch = useDispatch();
  const { value: expertise } = useSelector((state: any) => state.Expertise);
  const current_address = expertise.offline_location?.address || [];
  const coords = expertise.offline_location?.coords && typeof expertise.offline_location.coords.lat === 'number'
    ? expertise.offline_location.coords
    : DEFAULT_COORDS;

  const onDragOfflineLocation = async (location: any) => {
    const { address, full_address, coords: newCoords, label } = await getDragLocation(location);
    dispatch(set_expertise_data({
      ...expertise,
      offline_location: { coords: newCoords, address, full_address, label: label || expertise.offline_location?.label || '' }
    }));
  };

  const updateOfflineLocationForm = async () => {
    const { id, offline_location } = expertise;
    const resp = await updateHealthCare({ id, offline_location: JSON.stringify(offline_location) });
    ToastAlert(resp, 'Lokasi Berhasil di update');
  };

  const getCurrentUserLocation = () => {
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const newCoords = { lat: latitude, lng: longitude };

          const place: any = await getAddressFromCoords(newCoords);
          const address = place.address_components || [];
          const full_address = place.formatted_address || `${latitude}, ${longitude}`;
          const label = place.name || '';
          dispatch(set_expertise_data({
            ...expertise,
            offline_location: { coords: newCoords, address, full_address, label: label || expertise.offline_location?.label || '' }
          }));
        },
        (error) => {
          ToastAlert({ error }, `Error getting location: ${error.message}`);
        }
      );
    } else {
      ToastAlert({ error: 'Error' }, 'Geolocation is not supported by your browser');
    }
  };

  useEffect(() => {
    if (!expertise.offline_location?.label && !expertise.offline_location?.coords) {
      getCurrentUserLocation();
    }
  }, []);

  return (
    <DialogContent className="sm:max-w-[425px] lg:min-w-[500px] bg-white">
      <DialogHeader>
        <DialogTitle className="text-center">Tentukan Lokasi Layanan Offline</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 text-sm">
        <Input
          placeholder="Nama Lokasi (e.g. Klinik, Tempat Praktik)"
          value={expertise.offline_location?.label || ''}
          onChange={(e) => {
            dispatch(set_expertise_data({
              ...expertise,
              offline_location: { ...expertise.offline_location, label: e.target.value }
            }));
          }}
        />
        <div className="h-52 rounded-sm overflow-hidden border border-gray-200">
          <LeafletMapView
            center={coords}
            zoom={15}
            markerPosition={coords}
            draggable={true}
            onDragEnd={onDragOfflineLocation}
            onMapClick={onDragOfflineLocation}
          />
        </div>
        <div className="flex gap-2 items-end">
          <div className="w-full">
            <p className="mb-1 text-xs text-gray-600">Cari alamat lokasi layanan</p>
            <LocationSearchInput
              placeholder={expertise.offline_location?.full_address || "Cari alamat layanan offline..."}
              currentCoords={coords}
              onPlaceSelected={(place: PlaceSearchResult) => {
                const { address_components: address, full_address, coords: newCoords, name: label } = place;
                dispatch(set_expertise_data({
                  ...expertise,
                  offline_location: {
                    coords: newCoords,
                    address,
                    full_address,
                    label: expertise.offline_location?.label || label || ''
                  }
                }));
              }}
            />
          </div>
        </div>
        <div className="flex justify-between gap-2 items-center pt-1">
          <LocateFixed
            className="text-primary cursor-pointer hover:animate-pulse flex-shrink-0"
            onClick={getCurrentUserLocation}
          />
          <div className="w-3/4 overflow-hidden">
            <p className="font-medium text-xs text-gray-800 truncate">
              {getAddressLabel(current_address, 'administrative_area_level_2')}, {getAddressLabel(current_address, 'administrative_area_level_1')}
            </p>
            <p className="text-xs text-gray-400 truncate">{getAddressLabel(current_address, 'route')}</p>
          </div>
          <Button disabled={!expertise.offline_location?.label} onClick={updateOfflineLocationForm}>Terapkan</Button>
        </div>
      </div>
    </DialogContent>
  );
}

export default DialogLocationOffline;
